import { type RuleGroup, describeGroup, isRunnable } from "@trackmark/core";
import { Link } from "@tanstack/react-router";
import { RuleBuilder } from "./RuleBuilder.js";
import styles from "./Lists.module.css";

/**
 * Lists: the index (§J13) and the dynamic-list builder (design/README.md §10).
 *
 * §J13 settles what the index is: *"Columns: name, kind, item count (live for dynamic lists),
 * last updated. No card grid."* The count being live is the load-bearing part — a stored count on
 * a list called "Everything I rated 9+" starts lying at the next rating.
 *
 * The builder's own claim is the count in its header: `1,284` over `SONGS MATCH · UPDATES AS YOU
 * TYPE`. It is a promise about responsiveness, so the count is never stale-but-shown — while a
 * request is in flight the previous number holds rather than flickering to zero.
 */

export interface ListRow {
  id: number;
  name: string;
  kind: string;
  rules: RuleGroup;
  itemCount: number;
  updatedAt: string;
}

export interface PreviewSong {
  id: number;
  title: string;
  artist: string;
  album: string;
  albumId: number;
  score: number | null;
}

export interface ListsProps {
  lists: ListRow[];
  /** Opens an existing list in the builder. */
  /**
   * Opens a saved list IN THE BUILDER. Reached from the list's own screen now rather than from
   * the index, where the name goes to the songs — editing the rules is a different act from
   * looking at what they select.
   */
  onOpen: (list: ListRow) => void;
  /** Starts a new one. Distinct from `onClose` — `onOpen(null)` meant both, and the New button
   *  closed the builder it was supposed to open. */
  onNew: () => void;
  onClose: () => void;
  onDelete: (id: number) => void;

  /** Non-null when a list is open in the builder. */
  editing: { id: number | null; name: string; rules: RuleGroup } | null;
  onName: (name: string) => void;
  onRules: (rules: RuleGroup) => void;
  onSave: () => void;
  saving: boolean;

  total: number | null;
  hints: { excludes: number }[];
  preview: PreviewSong[];
}

const dateLabel = (iso: string) => {
  const d = new Date(iso);
  const M = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];
  return `${String(d.getUTCDate()).padStart(2, "0")} ${M[d.getUTCMonth()]} ${String(d.getUTCFullYear() % 100).padStart(2, "0")}`;
};

export function Lists({
  lists,
  onOpen,
  onNew,
  onClose,
  onDelete,
  editing,
  onName,
  onRules,
  onSave,
  saving,
  total,
  hints,
  preview,
}: ListsProps) {
  if (editing === null) {
    return (
      <div className={styles.page}>
        <div className={styles.head}>
          <div className={styles.title}>Lists</div>
          <button type="button" className={styles.newButton} data-testid="new-list" onClick={onNew}>
            + New list
          </button>
        </div>

        {lists.length === 0 ? (
          <div className={styles.empty} data-testid="lists-empty">
            <div className={styles.emptyTitle}>No lists yet</div>
            <div className={styles.emptyHint}>A LIST IS A QUESTION ABOUT THE LIBRARY THAT KEEPS ANSWERING ITSELF.</div>
          </div>
        ) : (
          // §J13: the library's table, not a card grid.
          <div className={styles.table} data-testid="lists-table">
            <div className={styles.tableHead}>
              <div>NAME</div>
              <div>RULES</div>
              <div>KIND</div>
              <div style={{ textAlign: "right" }}>SONGS</div>
              <div style={{ textAlign: "right" }}>UPDATED</div>
              <div />
            </div>
            {lists.map((list) => (
              <div key={list.id} className={styles.tableRow} data-testid="list-row">
                {/* The name goes to the LIST, not to its rules. Opening a saved list to edit it
                    was the only thing you could do with one, which made "412 songs match" a
                    number you could never look at. */}
                <Link
                  to="/list/$listId"
                  params={{ listId: String(list.id) }}
                  className={styles.rowName}
                  data-testid="list-link"
                >
                  {list.name}
                </Link>
                <div className={styles.rowRules}>{describeGroup(list.rules)}</div>
                <div className={styles.rowMeta}>{list.kind.toUpperCase()}</div>
                <div className={styles.rowCount}>{list.itemCount.toLocaleString()}</div>
                <div className={styles.rowMeta}>{dateLabel(list.updatedAt)}</div>
                <button
                  type="button"
                  className={styles.rowRemove}
                  aria-label={`Delete ${list.name}`}
                  onClick={() => onDelete(list.id)}
                >
                  ×
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  const runnable = isRunnable(editing.rules);

  return (
    <div className={styles.page}>
      <div className={styles.head}>
        <input
          className={styles.nameInput}
          value={editing.name}
          aria-label="List name"
          placeholder="Untitled list"
          data-testid="list-name"
          onChange={(event) => onName(event.target.value)}
        />
        <button type="button" className={styles.backButton} data-testid="close-builder" onClick={onClose}>
          ← All lists
        </button>
        <button
          type="button"
          className={styles.saveButton}
          data-testid="save-list"
          disabled={saving || editing.name.trim() === ""}
          onClick={onSave}
        >
          {saving ? "Saving…" : "Save list"}
        </button>
      </div>

      <div className={styles.builderLayout}>
        <div className={styles.builderMain}>
          {/* The count is the header, in the design's own words. */}
          <div className={styles.matchBlock}>
            <div className={styles.matchCount} data-testid="match-count" data-stale={total === null || undefined}>
              {total === null ? "—" : total.toLocaleString()}
            </div>
            <div className={styles.matchLabel}>
              SONGS MATCH · UPDATES AS YOU TYPE
              {!runnable && <span className={styles.matchNote}> · A RULE IS UNFINISHED, SO THIS NUMBER IS HOLDING</span>}
            </div>
          </div>

          <RuleBuilder value={editing.rules} onChange={onRules} hints={hints} />
        </div>

        {/* The first twelve matches, beside the rules rather than under them: the point is to see
            the rule and its consequence at the same time. */}
        <div className={styles.preview} data-testid="list-preview">
          <div className={styles.previewHead}>FIRST {preview.length} MATCHES</div>
          {preview.length === 0 ? (
            <div className={styles.previewEmpty}>NOTHING MATCHES THESE RULES.</div>
          ) : (
            preview.map((song) => (
              <Link
                key={song.id}
                to="/album/$albumId"
                params={{ albumId: String(song.albumId) }}
                className={styles.previewRow}
                data-testid="preview-row"
              >
                <span className={styles.previewTitle}>{song.title}</span>
                <span className={styles.previewArtist}>{song.artist}</span>
                <span className={styles.previewScore}>{song.score ?? "—"}</span>
              </Link>
            ))
          )}
        </div>
      </div>
    </div>
  );
}
